import type { Event, EventDiagnosed } from "../../api/useEvents";
import { getEventFaultNames } from ".";

export const isDiagnosedEvent = (event: Event): event is EventDiagnosed =>
  event.type === "DIAGNOSED";

export const canEventBeUngrouped = (event: Event): event is EventDiagnosed =>
  isDiagnosedEvent(event) &&
  event.metadata.faults.length > 1 &&
  event.sourceFaultEventIds.length > 0;

export function isEventSelectableForGrouping({
  event,
  events,
  selectedEvents,
}: {
  event: Event;
  events: readonly Event[];
  selectedEvents: readonly EventDiagnosed[];
}): boolean {
  if (!isDiagnosedEvent(event)) return false;
  if (selectedEvents.some(selectedEvent => selectedEvent.id === event.id)) return true;

  const eventSignature = getFaultEventSignature(event);
  if (
    selectedEvents.some(selectedEvent => getFaultEventSignature(selectedEvent) === eventSignature)
  )
    return false;
  if (selectedEvents.length === 0) return true;
  const nextEventsToGroup = [...selectedEvents, event];

  const selectedEventIds = new Set(nextEventsToGroup.map(selectedEvent => selectedEvent.id));
  const nextSignature = getFailureNamesSignature(
    nextEventsToGroup.flatMap(groupedEvent => getEventFaultNames(groupedEvent)),
  );
  const nextStartsAt = Math.min(
    ...nextEventsToGroup.map(groupedEvent => groupedEvent.startsAt.getTime()),
  );
  const nextEndsAt = Math.max(
    ...nextEventsToGroup.map(groupedEvent => groupedEvent.endsAt.getTime()),
  );

  return !doesSignatureExistInGroupedPeriod({
    events,
    selectedEventIds,
    signature: nextSignature,
    startsAt: nextStartsAt,
    endsAt: nextEndsAt,
  });
}

function doesSignatureExistInGroupedPeriod({
  events,
  selectedEventIds,
  signature,
  startsAt,
  endsAt,
}: {
  events: readonly Event[];
  selectedEventIds: ReadonlySet<string>;
  signature: string;
  startsAt: number;
  endsAt: number;
}): boolean {
  return events.some(
    existingEvent =>
      isDiagnosedEvent(existingEvent) &&
      !selectedEventIds.has(existingEvent.id) &&
      getFaultEventSignature(existingEvent) === signature &&
      existingEvent.startsAt.getTime() <= endsAt &&
      startsAt <= existingEvent.endsAt.getTime(),
  );
}

function getFaultEventSignature(event: EventDiagnosed): string {
  return getFailureNamesSignature(getEventFaultNames(event));
}

function getFailureNamesSignature(failureNames: readonly string[]): string {
  return JSON.stringify([...new Set(failureNames)].sort((a, b) => a.localeCompare(b)));
}
